import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Mail, Sparkles, ArrowRight, ArrowDown } from 'lucide-react';
import { SITE } from '../mock/data';
import { useLang } from '../i18n';
import { publicApi, resolveImage } from '../api';
import { useFetch } from '../useFetch';
import HomeFeaturedCarousel from '../components/HomeFeaturedCarousel';
import HomeSearchBar from '../components/HomeSearchBar';
import PageMeta from '../components/PageMeta';

const HERO_IMAGE = 'https://customer-assets-v7afamib.emergentagent.net/job_yup-rebuild/artifacts/devapzeb_ChatGPT%20Image%203%20ago%202026%2C%2023_55_43.png';
// Legacy palm/sea image reused for the newsletter background (softer atmosphere below the fold)
const NEWSLETTER_BG = 'https://customer-assets-v7afamib.emergentagent.net/job_yup-rebuild/artifacts/tj5y31hc_s5aunpmnf1bvinp3ko3l.webp';

export default function Home() {
  const { t, lang } = useLang();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { data: tiles, loading } = useFetch('home-tiles', publicApi.homeTiles);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) return;
    setSubmitting(true);
    setError('');
    try {
      await publicApi.subscribe(email, 'it');
      setSent(true); setEmail('');
    } catch (err) {
      setError(t('sendError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageMeta
        title="YUP — Your Utility Points | La tua guida turistica digitale"
        description="Scopri cosa vedere, ristoranti, attività, esperienze e numeri di emergenza nelle destinazioni turistiche italiane. Tutto quello che ti serve, sempre a portata di mano."
      />
      {/* HERO — Logo + Welcome text overlaid on world landmarks cover */}
      <section
        className="relative w-full overflow-hidden"
        data-testid="home-hero-section"
      >
        <div className="relative w-full min-h-[70vh] md:min-h-[80vh] flex items-center justify-center">
          {/* Background image */}
          <img
            src={HERO_IMAGE}
            alt="Viaggia con YUP — le migliori destinazioni del mondo"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = SITE.heroBg; }}
          />
          {/* Multi-layer overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/35 to-black/80" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.55)_75%)]" />

          {/* Foreground content */}
          <div
            className="relative z-10 max-w-5xl mx-auto px-6 py-14 md:py-20 text-center text-white flex flex-col items-center"
            data-testid="home-hero-content"
          >
            <img
              src={SITE.logo}
              alt="YUP logo"
              className="h-24 sm:h-28 md:h-40 w-auto drop-shadow-[0_10px_25px_rgba(0,0,0,0.65)] fade-in-up"
              data-testid="home-hero-logo"
            />

            <h2 className="mt-8 text-xs md:text-sm tracking-[0.35em] font-medium text-white/95 fade-in-up fade-in-up-delay-1">
              {t('tagline')}
            </h2>
            <h1
              className="mt-4 text-4xl md:text-6xl tagline-display fade-in-up fade-in-up-delay-2"
              style={{ textShadow: '0 4px 24px rgba(0,0,0,0.7)' }}
            >
              {t('welcome')}
            </h1>
            <p className="mt-5 text-white/85 text-sm md:text-base max-w-2xl fade-in-up fade-in-up-delay-3"
               style={{ textShadow: '0 2px 12px rgba(0,0,0,0.7)' }}>
              {t('heroSubtitle')}
            </p>

            {/* Primary CTA — scroll to cities section */}
            <a
              href="#cities"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('cities')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-bold text-black transition-all hover:scale-105 fade-in-up fade-in-up-delay-3"
              style={{
                background: 'linear-gradient(135deg, var(--yup-primary), #d4c599)',
                boxShadow: '0 12px 30px -10px rgba(188,172,138,0.55)'
              }}
              data-testid="home-hero-cta"
            >
              <span>Esplora le città</span>
              <ArrowDown size={18} />
            </a>

            {/* Subtle scroll hint */}
            <div className="mt-8 flex flex-col items-center gap-2 text-white/50 text-xs tracking-[0.3em] uppercase fade-in-up fade-in-up-delay-3">
              <span>{t('discover')}</span>
              <div className="w-px h-8 bg-gradient-to-b from-[color:var(--yup-primary)] to-transparent animate-pulse" />
            </div>
          </div>
        </div>
      </section>

      {/* Global search bar — quick access to any activity/category */}
      <HomeSearchBar />

      {/* Featured activities carousel — "Esperienze da non perdere" */}
      <HomeFeaturedCarousel />

      {/* Cities section */}
      <section id="cities" className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 scroll-mt-24" data-testid="home-cities-section">
        {/* Section header — big "LE CITTÀ" title with sub-tagline */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[color:var(--yup-primary)]/40 bg-[color:var(--yup-primary)]/10 text-[color:var(--yup-primary)] text-[10px] tracking-[0.3em] uppercase mb-4">
            <span>YUP · Destinazioni</span>
          </div>
          <h2
            className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight tracking-wide"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="home-cities-title"
          >
            {t('citiesTitle')}
          </h2>
          <p className="mt-3 text-white/60 text-sm sm:text-base tracking-[0.25em] uppercase">
            {t('travelWithUs')}
          </p>
        </div>
        {loading && <div className="flex justify-center py-10"><Loader2 className="animate-spin text-gray-400" size={32} /></div>}
        <div className="grid grid-cols-1 gap-4 sm:gap-6 md:gap-8 mt-6 sm:mt-10">
          {tiles?.map((tile, idx) => {
            // A tile is either a `zone` (macro-area grouping several cities)
            // or a `city` (single-destination card, either standalone or a
            // spotlighted city that also belongs to a zone).
            const key = `${tile.type}-${tile.slug}`;
            const href = tile.type === 'zone' ? `/zone/${tile.slug}` : `/${tile.slug}`;
            const isSoon = tile.type === 'city' && tile.status !== 'active';
            // Zone cards use the localized display name (auto-translated by
            // the backend into 7 languages). City cards keep the short uppercase
            // brand name that is already country-agnostic.
            const label = tile.type === 'zone'
              ? (tile.full_name?.[lang] || tile.full_name?.it || tile.name)
              : tile.name;
            const testid = `home-${tile.type}-card-${tile.slug}`;
            return (
              <div key={key} className="fade-in-up" style={{ animationDelay: `${0.1 + idx * 0.1}s` }}>
                {isSoon ? (
                  <div className="city-card block relative cursor-not-allowed" data-testid={testid}>
                    <img src={resolveImage(tile.image)} alt={label} loading="lazy" decoding="async" className="w-full h-[160px] sm:h-[240px] md:h-[380px] object-cover filter grayscale-[35%]" />
                    <div className="overlay" />
                    <div className="label">{label}<div className="soon-badge mt-2">{t('soon')}</div></div>
                  </div>
                ) : (
                  <Link to={href} className="city-card block" data-testid={testid}>
                    <img src={resolveImage(tile.image)} alt={label} loading="lazy" decoding="async" className="w-full h-[160px] sm:h-[240px] md:h-[380px] object-cover" />
                    <div className="overlay" />
                    <div className="label">
                      {label}
                      {tile.type === 'zone' && (
                        <div className="text-xs sm:text-sm text-white/80 tracking-[0.2em] uppercase mt-2 font-normal">
                          {tile.city_count} {tile.city_count === 1 ? 'città' : 'città'}
                        </div>
                      )}
                    </div>
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* NEWSLETTER — Full-width glassmorphism section */}
      <section
        className="relative w-full overflow-hidden"
        data-testid="home-newsletter-section"
      >
        {/* Background image + overlays */}
        <div className="absolute inset-0">
          <img
            src={NEWSLETTER_BG}
            alt=""
            className="w-full h-full object-cover"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/75 to-black/95" />
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(188,172,138,0.25) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(188,172,138,0.15) 0%, transparent 55%)'
            }}
          />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 py-24 md:py-32">
          {/* Glass card */}
          <div
            className="relative rounded-2xl border border-[color:var(--yup-primary)]/30 backdrop-blur-xl bg-white/[0.04] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] px-6 py-12 md:px-14 md:py-16 overflow-hidden"
            data-testid="newsletter-card"
          >
            {/* Gold gradient accent border */}
            <div
              className="absolute -top-px left-8 right-8 h-px"
              style={{ background: 'linear-gradient(90deg, transparent, var(--yup-primary), transparent)' }}
            />

            <div className="flex flex-col items-center text-center">
              {/* Sparkle badge */}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[color:var(--yup-primary)]/40 bg-[color:var(--yup-primary)]/10 text-[color:var(--yup-primary)] text-[11px] tracking-[0.3em] uppercase mb-6">
                <Sparkles size={14} />
                <span>{t('newsletterBadge')}</span>
              </div>

              <h3
                className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="newsletter-title"
              >
                {t('newsletterTitle1')} <span className="text-[color:var(--yup-primary)] italic">{t('newsletterTitleHighlight')}</span> {t('newsletterTitle2')}
              </h3>
              <p className="text-white/70 text-sm md:text-base max-w-xl mb-10 leading-relaxed">
                {t('newsletterDesc')}
              </p>

              {sent ? (
                <div
                  className="flex items-center gap-3 px-6 py-4 rounded-xl bg-[color:var(--yup-primary)]/15 border border-[color:var(--yup-primary)]/40 text-[color:var(--yup-primary)] font-semibold"
                  data-testid="newsletter-success"
                >
                  <CheckCircle2 size={22} />
                  <span>{t('formSent')} {t('newsletterSuccessExtra')}</span>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  className="w-full max-w-lg flex flex-col sm:flex-row gap-3 items-stretch"
                  data-testid="newsletter-form"
                >
                  <div className="relative flex-1">
                    <Mail
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
                    />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder={t('emailPlaceholder')}
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/5 border border-white/15 text-white placeholder:text-white/40 focus:outline-none focus:border-[color:var(--yup-primary)] focus:bg-white/10 focus:ring-2 focus:ring-[color:var(--yup-primary)]/25 transition-all"
                      data-testid="newsletter-email-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-bold text-black transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{
                      background: 'linear-gradient(135deg, var(--yup-primary), #d4c599)',
                      boxShadow: '0 10px 30px -10px rgba(188,172,138,0.6)'
                    }}
                    data-testid="newsletter-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        <span>{t('sending')}</span>
                      </>
                    ) : (
                      <>
                        <span>{t('signUp')}</span>
                        <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                </form>
              )}

              {error && (
                <p className="mt-4 text-red-400/90 text-sm" data-testid="newsletter-error">{error}</p>
              )}

              {/* Trust line */}
              <p className="mt-6 text-[11px] text-white/40 tracking-wider uppercase">
                {t('trustLine')}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
